import { useCallback, useSyncExternalStore } from "react";
import { tablesSubscription } from "@/lib/firestore/subscriptions/tables";
import { NO_WEDDING_KEY, planScopedKey } from "@/lib/firestore/subscription-keys";
import {
  EMPTY_TABLES,
  TABLES_SNAPSHOT_EMPTY,
  TABLES_SNAPSHOT_LOADING,
} from "@/lib/firestore/subscription-snapshots";
import { useWedding } from "./useWedding";
import type { Table } from "@amazing-wedding/shared-types";

interface UseTablesResult {
  tables: Table[];
  loading: boolean;
}

/**
 * Subscribes to /weddings/{wid}/seatingPlans/{planId}/tables.
 * Returns empty when planId is null (migration / no active plan).
 *
 * Backed by a refcounted shared Firestore subscription keyed by weddingId::planId.
 */
export function useTables(planId: string | null | undefined): UseTablesResult {
  const { wedding, loading: weddingLoading } = useWedding();
  const weddingId = wedding?.id;
  const ready = Boolean(weddingId && planId);
  const key = ready ? planScopedKey(weddingId!, planId!) : NO_WEDDING_KEY;

  const subscribe = useCallback(
    (listener: () => void) => {
      if (weddingLoading || !ready) return () => {};
      return tablesSubscription.subscribe(key, listener);
    },
    [weddingLoading, ready, key],
  );

  const getSnapshot = useCallback(() => {
    if (weddingLoading) return TABLES_SNAPSHOT_LOADING;
    if (!ready) return TABLES_SNAPSHOT_EMPTY;
    return tablesSubscription.getSnapshot(key);
  }, [weddingLoading, ready, key]);

  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => TABLES_SNAPSHOT_LOADING);

  if (weddingLoading) {
    return { tables: EMPTY_TABLES, loading: true };
  }
  if (!ready) {
    return { tables: EMPTY_TABLES, loading: false };
  }

  return { tables: snapshot.data, loading: snapshot.loading };
}
